import React from "react";
import { Alert, AlertDescription } from "../../../components/shadcn/ui/alert";
import { Badge } from "../../../components/shadcn/ui/badge";
import { Card } from "../../../components/shadcn/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/shadcn/ui/select";

/** Informational, not an alarm: role="note" keeps it out of live-region announcements. */
export function Notice({ children }: { children: React.ReactNode }) {
  return (
    <Alert role="note" className="fw-notice">
      <AlertDescription>{children}</AlertDescription>
    </Alert>
  );
}

export type BadgeTone = "green" | "amber" | "rose" | "indigo" | "neutral";

export function StatusBadge({
  tone = "neutral",
  children,
}: {
  tone?: BadgeTone;
  children: React.ReactNode;
}) {
  return (
    <Badge variant="outline" className={`fw-badge ${tone}`}>
      {children}
    </Badge>
  );
}

export interface FieldOption {
  value: string;
  label: string;
  disabled?: boolean;
}

/** Radix rejects an empty item value, so "none" travels as a sentinel. */
const NONE = "__none__";

export function FieldSelect({
  label,
  value,
  options,
  onChange,
  disabled,
  className,
}: {
  label: string;
  value: string;
  options: FieldOption[];
  onChange: (value: string) => void;
  disabled?: boolean;
  className?: string;
}) {
  return (
    <Select
      value={value === "" ? NONE : value}
      onValueChange={(v) => onChange(v === NONE ? "" : v)}
      disabled={disabled}
    >
      <SelectTrigger aria-label={label} className={`fw-select ${className ?? ""}`}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="fw-select-menu">
        {options.map((o) => (
          <SelectItem
            key={o.value || NONE}
            value={o.value === "" ? NONE : o.value}
            disabled={o.disabled}
          >
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export function Section({
  title,
  subtitle,
  action,
  children,
}: {
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Card className="fw-card">
      <div className="fw-card-head">
        <div>
          <h2>{title}</h2>
          {subtitle && <p>{subtitle}</p>}
        </div>
        {action}
      </div>
      {children}
    </Card>
  );
}
